import { useTranslation } from 'react-i18next'

import { HTML_LINK_TARGET } from '../../../../../constants/html'
import {
  useContentOverflow,
  useIsTeamVisibilityHidden,
  useIsUserProfileNGAvailable,
  useUserData,
} from '../../../../../hooks'
import { preventDefault } from '../../../../../utils/events'
import { getInhabitantProfilePathname } from '../../../../../utils/link'
import {
  getUserListItemContentText,
  isTeamUserProfileHidden,
  isUserStatusBlocked,
} from '../../../../../utils/user'
import { Avatar } from '../../../../Avatar'
import { Badge, BadgeSize } from '../../../../Badge'
import { FloatingTooltip } from '../../../../Floating'
import { Link } from '../../../../Link'
import {
  getUkTextContent,
  toUkItemContent,
  toUserListItemContent,
} from '../../../../ListItem'
import {
  isSystemPostType,
  isTvTickerPostType,
  isWelcomePostType,
} from '../../../Newsboard.utils'
import Text from '../././../../../Text'
import { NewsboardPostHeaderPinAction } from '../NewsboardPostHeaderPinAction'

import { QA_NAME_POST_CREATOR } from './NewsboardPostHeaderContent.constants'
import { PostInfo } from './PostInfo'

import * as styles from './NewsboardPostHeaderContent.module.css'

function NewsboardPostHeaderContent({ data, unread, onReload }) {
  const { t } = useTranslation()

  const user = toUserListItemContent(data.owner)

  const uk = toUkItemContent(data.uk)

  const { overflow, setContentOverflowRef } = useContentOverflow()

  const isUserProfileNGAvailable = useIsUserProfileNGAvailable()

  const currentUser = useUserData()
  const isTeamHidden = useIsTeamVisibilityHidden()

  const { avatar, avatarColor, fullname, id } = user
  const { ukAvatar, ukName } = uk

  const userTextContent = getUserListItemContentText(user)

  const isTvTickerPost = isTvTickerPostType(data)

  const isSystemPost = isSystemPostType(data)

  const isUserBlocked = isUserStatusBlocked(user.status)

  const isWelcomePost = isWelcomePostType(data)

  function getPostCreatorText() {
    if (user.fullname) {
      return userTextContent
    } else {
      return getUkTextContent(uk)
    }
  }

  const postCreatorText = getPostCreatorText()

  function getAvatarContent() {
    if (isWelcomePost) {
      return { avatarText: ukName, src: ukAvatar }
    }

    return { avatarText: fullname, src: avatar }
  }

  const { avatarText, src } = getAvatarContent()

  const isOwnerProfileHidden = isTeamUserProfileHidden({
    currentUser,
    isTeamHidden,
    user,
  })

  const isOwnerLinkDisabled = isWelcomePost || isOwnerProfileHidden

  const linkProps = {
    ...(isOwnerLinkDisabled ? { tabIndex: -1, onClick: preventDefault } : null),
    className: isOwnerLinkDisabled ? styles.disableLink : undefined,
    rel: 'noreferrer',
    reloadDocument: true,
    target: HTML_LINK_TARGET.blank,
    to: getInhabitantProfilePathname({ id, isUserProfileNGAvailable }),
  }

  return (
    <div className={styles.NewsboardPostHeaderUser}>
      <div className={styles.infoContainer}>
        <Link {...linkProps}>
          <Avatar
            avatarSize={32}
            color={avatarColor}
            qaName={QA_NAME_POST_CREATOR}
            src={src}
            text={avatarText}
          />
        </Link>
        <div className={styles.textContainer}>
          <FloatingTooltip content={postCreatorText} disabled={!overflow}>
            <Link {...linkProps}>
              <Text
                className={styles.userName}
                qaName={QA_NAME_POST_CREATOR}
                ref={setContentOverflowRef}
              >
                {postCreatorText}
              </Text>
            </Link>
          </FloatingTooltip>
          {isUserBlocked ? (
            <Text
              isSecondary
            >{`(${t('Newsboard.PostOwnerUserBlockedKey')})`}</Text>
          ) : null}
          <PostInfo data={data} />
        </div>
      </div>
      <div className={styles.pinContainer}>
        {unread ? (
          <Badge className={styles.unreadBadge} size={BadgeSize.Big} />
        ) : null}
        {!isTvTickerPost && !isSystemPost && !isWelcomePost ? (
          <NewsboardPostHeaderPinAction data={data} onReload={onReload} />
        ) : null}
      </div>
    </div>
  )
}

export default NewsboardPostHeaderContent
